import { Component } from '@theme/component';

/**
 * Product tabs: a strip of tab buttons that each reveal one panel of
 * merchant-authored theme blocks below the product.
 *
 * The tab buttons are NOT server-rendered. Horizon's `section.blocks` proxy
 * exposes each `content_for`-rendered block's `id`, but reports its `type` as
 * `@theme` and exposes none of its `settings` — so the parent section cannot
 * read each `_product-tab`'s `label` in order to build the tablist in Liquid.
 * Each panel therefore publishes its own label on `data-tab-label`, and this
 * component builds the matching buttons from the panels on connect.
 *
 * Tabs are queried from the DOM rather than read off `this.refs`, because the
 * buttons are created here and the base class's ref collection is not
 * guaranteed to have observed them yet at that point.
 *
 * Activation (`on:click`) and keyboard navigation (`on:keydown`) are set as
 * attributes on the generated buttons and dispatched by the base `Component`
 * class's single document-level delegated listener (see `@theme/component`),
 * so this class adds no listeners of its own and has nothing beyond the base
 * class's teardown to release.
 *
 * Every panel's content stays in the DOM — inactive panels carry the `hidden`
 * attribute rather than being removed — so tab content remains indexable.
 *
 * @typedef {object} ProductTabsRefs
 * @property {HTMLElement[]} [panels] - The tab panels, one per tab, in DOM order.
 *
 * @extends {Component<ProductTabsRefs>}
 */
export class ProductTabsComponent extends Component {
  /**
   * Builds the tab buttons from the server-rendered panels, then lets the base
   * class finish connecting.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#buildTabs();
  }

  /**
   * The generated tab buttons add no listeners of their own, and this
   * component owns no observers or timers beyond the base class's, which
   * `super.disconnectedCallback()` releases. Declared explicitly so that
   * stays true on purpose rather than by accident.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Activates the tab that was clicked.
   *
   * @param {Event} event - The click event from the `on:click="/selectTab"` binding.
   */
  selectTab(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tab = target.closest('[role="tab"]');
    if (!tab) return;

    const index = this.#tabs().indexOf(/** @type {HTMLElement} */ (tab));
    if (index === -1) return;

    this.#activate(index);
  }

  /**
   * Roving-tabindex keyboard navigation for the tablist. ArrowLeft/ArrowRight
   * and Home/End move focus between tabs without changing the active panel
   * (the manual-activation ARIA tabs pattern); Enter/Space activation is
   * handled for free by the native `<button>` via `on:click`.
   *
   * @param {KeyboardEvent} event - The keydown event from the `on:keydown="/handleTabKeydown"` binding.
   */
  handleTabKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const tabs = this.#tabs();
    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (target.closest('[role="tab"]')));
    if (currentIndex === -1) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#focusTab(nextIndex);
  }

  /**
   * @returns {HTMLElement[]} The tab buttons, in DOM order.
   */
  #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /**
   * @returns {HTMLElement[]} The tab panels, in DOM order.
   */
  #panels() {
    const { panels } = this.refs;
    if (panels?.length) return panels;
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.product-tabs__panel')]);
  }

  /**
   * Creates one `<button role="tab">` per panel, reusing the ids the panel
   * already declares so `aria-labelledby` / `aria-controls` stay paired. The
   * initially visible panel (the one the section rendered without `hidden`)
   * becomes the selected tab, so the tabs match the server-rendered state.
   *
   * A panel whose `label` the merchant left blank is skipped rather than given
   * an unlabelled button, and is unhidden so its content is never stranded
   * behind a tab that does not exist.
   */
  #buildTabs() {
    const tablist = this.querySelector('[role="tablist"]');
    if (!tablist) return;

    const panels = this.#panels();
    if (!panels.length) return;

    // Re-entrant safety: the theme editor can reconnect a section in place.
    tablist.replaceChildren();

    const fragment = document.createDocumentFragment();

    for (const panel of panels) {
      const label = panel.dataset.tabLabel;

      if (!label) {
        panel.hidden = false;
        continue;
      }

      const isSelected = !panel.hidden;
      const button = document.createElement('button');

      button.type = 'button';
      button.textContent = label;
      button.className = 'product-tabs__tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('ref', 'tabs[]');
      button.setAttribute('on:click', '/selectTab');
      button.setAttribute('on:keydown', '/handleTabKeydown');
      button.setAttribute('aria-selected', String(isSelected));
      button.setAttribute('tabindex', isSelected ? '0' : '-1');
      button.setAttribute('aria-controls', panel.id);

      const tabId = panel.getAttribute('aria-labelledby');
      if (tabId) button.id = tabId;

      fragment.append(button);
    }

    tablist.append(fragment);
    this.setAttribute('data-tabs-ready', 'true');
  }

  /**
   * Moves the roving tabindex to `index` and focuses that tab, without
   * changing which panel is selected.
   *
   * @param {number} index
   */
  #focusTab(index) {
    const tabs = this.#tabs();
    const nextTab = tabs[index];
    if (!nextTab) return;

    for (const [i, tab] of tabs.entries()) {
      tab.setAttribute('tabindex', i === index ? '0' : '-1');
    }

    nextTab.focus();
  }

  /**
   * Selects tab `index` and reveals the panel it controls.
   *
   * Panels are matched by `aria-controls` rather than by position, so a panel
   * skipped for having no label cannot shift the pairing.
   *
   * @param {number} index
   */
  #activate(index) {
    const tabs = this.#tabs();
    const activeTab = tabs[index];
    if (!activeTab) return;

    for (const [i, tab] of tabs.entries()) {
      const isSelected = i === index;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.setAttribute('tabindex', isSelected ? '0' : '-1');
    }

    const controlledIds = new Set(tabs.map((tab) => tab.getAttribute('aria-controls')));
    const activeId = activeTab.getAttribute('aria-controls');

    for (const panel of this.#panels()) {
      if (!controlledIds.has(panel.id)) continue;
      panel.hidden = panel.id !== activeId;
    }

    activeTab.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
